import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { CheckCircle2, XCircle, Clock, Download, Eye, X } from "lucide-react";

const badgeFor = (s) => ({ Pending: "badge-yellow", Successful: "badge-green", Failed: "badge-red" }[s] || "badge-gray");

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [modal, setModal] = useState(null); // {payment}
  const [viewer, setViewer] = useState(null); // {url, mime}
  const [action, setAction] = useState({ status: "Successful", admin_note: "" });

  const load = async () => {
    const r = await api.get("/payments");
    setPayments(r.data);
  };
  useEffect(() => { load(); }, []);

  const openVerify = (p) => { setAction({ status: p.status === "Pending" ? "Successful" : p.status, admin_note: p.admin_note || "" }); setModal({ payment: p }); };
  const close = () => setModal(null);

  const submitVerify = async (e) => {
    e.preventDefault();
    await api.patch(`/payments/${modal.payment.id}`, action);
    close(); load();
  };

  const viewScreenshot = async (p) => {
    if (!p.screenshot_url) return;
    const token = localStorage.getItem("crm_token");
    const res = await fetch(`${API_BASE}${p.screenshot_url.replace("/api", "")}`, {
      headers: { Authorization: `Bearer ${token}` }, credentials: "include",
    });
    const blob = await res.blob();
    setViewer({ url: URL.createObjectURL(blob), mime: blob.type, name: `${p.payment_number}` });
  };

  const closeViewer = () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); };

  const downloadReceipt = async (p) => {
    const token = localStorage.getItem("crm_token");
    const res = await fetch(`${API_BASE}/payments/${p.id}/receipt`, {
      headers: { Authorization: `Bearer ${token}` }, credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${p.payment_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const pending = payments.filter((p) => p.status === "Pending").length;
  const success = payments.filter((p) => p.status === "Successful").length;
  const failed = payments.filter((p) => p.status === "Failed").length;
  const totalReceived = payments.filter((p) => p.status === "Successful").reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Payments</h1>
        <p className="text-sm text-muted-foreground">Verify connector payments and issue receipts</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Received</div><div className="font-heading text-3xl font-extrabold mt-2 tabular">₹{totalReceived.toFixed(0)}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><Clock size={12} /> Pending</div><div className="font-heading text-3xl font-extrabold mt-2 tabular">{pending}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><CheckCircle2 size={12} /> Successful</div><div className="font-heading text-3xl font-extrabold mt-2 tabular">{success}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><XCircle size={12} /> Failed</div><div className="font-heading text-3xl font-extrabold mt-2 tabular">{failed}</div></div>
      </div>

      <div className="card overflow-hidden">
        <table className="table-clean">
          <thead><tr><th>Receipt No.</th><th>Connector</th><th>Amount</th><th>UTR</th><th>Date</th><th>Status</th><th>Proof</th><th className="text-right">Actions</th></tr></thead>
          <tbody data-testid="payments-table-body">
            {payments.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-8">No payments yet</td></tr>
            ) : payments.map((p) => (
              <tr key={p.id}>
                <td className="font-mono font-semibold">{p.payment_number}</td>
                <td>{p.connector_name}<div className="text-xs text-muted-foreground">{p.connector_username}</div></td>
                <td className="tabular font-semibold">₹{p.amount.toFixed(2)}</td>
                <td className="font-mono text-xs">{p.utr || "—"}</td>
                <td className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                <td><span className={`badge ${badgeFor(p.status)}`}>{p.status}</span></td>
                <td>
                  {p.screenshot_url ? (
                    <button className="btn btn-ghost !p-2" onClick={() => viewScreenshot(p)} data-testid={`view-proof-${p.payment_number}`} title="View proof"><Eye size={16} /></button>
                  ) : <span className="text-xs text-muted-foreground">None</span>}
                </td>
                <td className="text-right whitespace-nowrap space-x-1">
                  <button className="btn btn-outline !py-1.5 !px-3" onClick={() => openVerify(p)} data-testid={`verify-${p.payment_number}`}>Review</button>
                  <button className="btn btn-ghost !p-2" onClick={() => downloadReceipt(p)} title="Receipt PDF" data-testid={`receipt-${p.payment_number}`}><Download size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="card p-6 w-full max-w-md fade-in" onClick={(e) => e.stopPropagation()} data-testid="payment-verify-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Verify {modal.payment.payment_number}</h3>
              <button className="btn btn-ghost !p-1.5" onClick={close}><X size={16} /></button>
            </div>
            <div className="text-sm space-y-1 mb-4 bg-muted/50 p-3 rounded-md">
              <div><strong>Connector:</strong> {modal.payment.connector_name}</div>
              <div><strong>Amount:</strong> ₹{modal.payment.amount.toFixed(2)}</div>
              <div><strong>UTR:</strong> {modal.payment.utr || "—"}</div>
              <div><strong>Note:</strong> {modal.payment.note || "—"}</div>
            </div>
            <form onSubmit={submitVerify} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Status</label>
                <select className="input mt-1" value={action.status} onChange={(e) => setAction({ ...action, status: e.target.value })} data-testid="verify-status-select">
                  <option value="Successful">Successful</option>
                  <option value="Failed">Failed</option>
                  <option value="Pending">Keep Pending</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Admin Note</label>
                <textarea className="input mt-1 min-h-[70px]" value={action.admin_note} onChange={(e) => setAction({ ...action, admin_note: e.target.value })} data-testid="verify-note" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn btn-outline" onClick={close}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="verify-submit">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeViewer}>
          <div className="bg-card rounded-xl max-w-4xl max-h-[92vh] w-full flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="font-heading font-bold">Payment Proof · {viewer.name}</div>
              <button className="btn btn-ghost !p-2" onClick={closeViewer}><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center bg-black/5 dark:bg-black/40">
              {(viewer.mime || "").startsWith("image/") ? (
                <img src={viewer.url} alt="proof" className="max-w-full max-h-[80vh] object-contain" />
              ) : (
                <iframe src={viewer.url} title="proof" className="w-full h-[80vh] bg-white" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
